import { TransformControls } from '@react-three/drei';
import { type ThreeEvent, useThree } from '@react-three/fiber';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BoxGeometry,
  CapsuleGeometry,
  ConeGeometry,
  CylinderGeometry,
  EdgesGeometry,
  Group,
  MeshStandardMaterial,
  Object3D,
  PlaneGeometry,
  SphereGeometry,
} from 'three';

import { usePlayerCameraControls } from '@cartwhl/3d/player';

import {
  disposeObject3D,
  disposeObjectMaterials,
  floorAndCenterImportedObject,
  isRenderableObject3D,
  loadWorldAssetObject,
} from './assetLoading';
import {
  WORLD_POINTER_PLANE_SIZE,
  WORLD_TRANSFORM_CONTROL_SIZE,
} from './constants';
import type {
  MotionEditorWorldAssetObject,
  MotionEditorWorldObject,
  MotionEditorWorldObjectTransform,
  MotionEditorWorldPointer,
  MotionEditorWorldPrimitiveObject,
  MotionEditorWorldPrimitiveType,
  MotionEditorWorldTransformMode,
} from './types';

type CameraControlsWithEnabled = {
  enabled?: boolean;
  cancel?: () => void;
  stop?: () => void;
};

const CANVAS_CLICK_DESELECT_MOVE_THRESHOLD_PX = 5;
const ignoreRaycast = () => {};

interface PointerState {
  pointerId: number;
  clientX: number;
  clientY: number;
  moved: boolean;
}

interface ObjectSelectionPointerState {
  pointerId: number;
  objectId: string;
}

interface MotionEditorWorldObjectLayerProps {
  hoveredObjectId: string | null;
  objects: readonly MotionEditorWorldObject[];
  onHoverObject: (objectId: string | null) => void;
  onPointerUpdate: (pointer: MotionEditorWorldPointer) => void;
  onSelectObject: (objectId: string | null) => void;
  onTransformObject: (
    objectId: string,
    transform: MotionEditorWorldObjectTransform,
  ) => void;
  selectedObjectId: string | null;
  transformMode: MotionEditorWorldTransformMode;
}

export function MotionEditorWorldObjectLayer({
  hoveredObjectId,
  objects,
  onHoverObject,
  onPointerUpdate,
  onSelectObject,
  onTransformObject,
  selectedObjectId,
  transformMode,
}: MotionEditorWorldObjectLayerProps) {
  const gl = useThree((state) => state.gl);
  const canvasPointerRef = useRef<PointerState | null>(null);
  const objectSelectionPointerRef = useRef<ObjectSelectionPointerState | null>(
    null,
  );
  const suppressNextCanvasDeselectRef = useRef(false);
  const suppressNextCanvasDeselect = useCallback(() => {
    suppressNextCanvasDeselectRef.current = true;
  }, []);
  const beginObjectSelection = useCallback(
    (objectId: string, event: ThreeEvent<PointerEvent>) => {
      objectSelectionPointerRef.current = {
        objectId,
        pointerId: event.nativeEvent.pointerId,
      };
    },
    [],
  );

  useEffect(() => {
    const canvas = gl.domElement;

    const handlePointerDown = (event: PointerEvent) => {
      canvasPointerRef.current = {
        pointerId: event.pointerId,
        clientX: event.clientX,
        clientY: event.clientY,
        moved: false,
      };
    };

    const handlePointerMove = (event: PointerEvent) => {
      const pointer = canvasPointerRef.current;
      if (!pointer || pointer.pointerId !== event.pointerId) return;

      const deltaX = event.clientX - pointer.clientX;
      const deltaY = event.clientY - pointer.clientY;
      const moveThresholdSquared =
        CANVAS_CLICK_DESELECT_MOVE_THRESHOLD_PX *
        CANVAS_CLICK_DESELECT_MOVE_THRESHOLD_PX;
      if (deltaX * deltaX + deltaY * deltaY > moveThresholdSquared) {
        pointer.moved = true;
      }
    };

    const handlePointerUp = (event: PointerEvent) => {
      const pointer = canvasPointerRef.current;
      const objectSelectionPointer = objectSelectionPointerRef.current;
      canvasPointerRef.current = null;
      objectSelectionPointerRef.current = null;

      if (!pointer || pointer.pointerId !== event.pointerId) {
        suppressNextCanvasDeselectRef.current = false;
        return;
      }

      const suppressDeselect = suppressNextCanvasDeselectRef.current;
      suppressNextCanvasDeselectRef.current = false;
      if (pointer.moved || suppressDeselect) return;

      if (objectSelectionPointer?.pointerId === event.pointerId) {
        onSelectObject(objectSelectionPointer.objectId);
        return;
      }

      onSelectObject(null);
    };
    const clearPointerState = () => {
      canvasPointerRef.current = null;
      objectSelectionPointerRef.current = null;
      suppressNextCanvasDeselectRef.current = false;
    };

    canvas.addEventListener('pointerdown', handlePointerDown);
    canvas.addEventListener('pointermove', handlePointerMove);
    canvas.addEventListener('pointerup', handlePointerUp);
    canvas.addEventListener('pointerleave', clearPointerState);
    canvas.addEventListener('pointercancel', clearPointerState);

    return () => {
      canvas.removeEventListener('pointerdown', handlePointerDown);
      canvas.removeEventListener('pointermove', handlePointerMove);
      canvas.removeEventListener('pointerup', handlePointerUp);
      canvas.removeEventListener('pointerleave', clearPointerState);
      canvas.removeEventListener('pointercancel', clearPointerState);
    };
  }, [gl.domElement, onSelectObject]);

  return (
    <>
      <WorldPointerPlane onPointerUpdate={onPointerUpdate} />
      {objects.map((object) => (
        <MotionEditorWorldObjectRender
          key={object.id}
          hovered={hoveredObjectId === object.id}
          object={object}
          onBeginObjectSelection={beginObjectSelection}
          onHoverObject={onHoverObject}
          onTransformInteraction={suppressNextCanvasDeselect}
          onTransformObject={onTransformObject}
          selected={selectedObjectId === object.id}
          transformMode={transformMode}
        />
      ))}
    </>
  );
}

function WorldPointerPlane({
  onPointerUpdate,
}: Pick<MotionEditorWorldObjectLayerProps, 'onPointerUpdate'>) {
  const handlePointer = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      onPointerUpdate(pointerFromThreeEvent(event));
    },
    [onPointerUpdate],
  );

  return (
    <mesh
      position={[0, 0, 0]}
      rotation={[-Math.PI / 2, 0, 0]}
      onPointerMove={handlePointer}
      onPointerDown={(event) => {
        handlePointer(event);
      }}
    >
      <planeGeometry
        args={[WORLD_POINTER_PLANE_SIZE, WORLD_POINTER_PLANE_SIZE]}
      />
      <meshBasicMaterial transparent opacity={0} depthWrite={false} />
    </mesh>
  );
}

interface MotionEditorWorldObjectRenderProps {
  hovered: boolean;
  object: MotionEditorWorldObject;
  onBeginObjectSelection: (
    objectId: string,
    event: ThreeEvent<PointerEvent>,
  ) => void;
  onHoverObject: (objectId: string | null) => void;
  onTransformInteraction: () => void;
  onTransformObject: (
    objectId: string,
    transform: MotionEditorWorldObjectTransform,
  ) => void;
  selected: boolean;
  transformMode: MotionEditorWorldTransformMode;
}

function MotionEditorWorldObjectRender({
  hovered,
  object,
  onBeginObjectSelection,
  onHoverObject,
  onTransformInteraction,
  onTransformObject,
  selected,
  transformMode,
}: MotionEditorWorldObjectRenderProps) {
  const [groupObject, setGroupObject] = useState<Group | null>(null);
  const { cameraControls } = usePlayerCameraControls();
  const previousCameraEnabledRef = useRef<boolean | null>(null);
  const materialState = selected ? 'selected' : hovered ? 'hovered' : 'default';
  const visual = getWorldObjectVisual(materialState);

  const handleStartTransform = useCallback(() => {
    const controls = cameraControls as CameraControlsWithEnabled | null;
    if (!controls) return;

    if ('enabled' in controls) {
      previousCameraEnabledRef.current = controls.enabled ?? true;
      controls.enabled = false;
    }
    controls.cancel?.();
    controls.stop?.();
  }, [cameraControls]);

  const handleFinishTransform = useCallback(() => {
    const controls = cameraControls as CameraControlsWithEnabled | null;
    if (controls && previousCameraEnabledRef.current !== null) {
      controls.stop?.();
      controls.enabled = previousCameraEnabledRef.current;
    }
    previousCameraEnabledRef.current = null;

    if (groupObject) {
      onTransformObject(object.id, transformFromObject(groupObject));
    }
  }, [cameraControls, groupObject, object.id, onTransformObject]);

  const handleObjectChange = useCallback(() => {
    if (!groupObject) return;
    onTransformObject(object.id, transformFromObject(groupObject));
  }, [groupObject, object.id, onTransformObject]);

  return (
    <>
      <group
        ref={setGroupObject}
        name={object.name}
        visible={object.visible}
        position={object.transform.position}
        rotation={object.transform.rotation}
        scale={object.transform.scale}
        onPointerDown={(event) => {
          event.stopPropagation();
          if (event.button === 0) {
            onBeginObjectSelection(object.id, event);
          }
        }}
        onPointerOver={(event) => {
          event.stopPropagation();
          onHoverObject(object.id);
        }}
        onPointerOut={() => {
          onHoverObject(null);
        }}
      >
        {object.kind === 'primitive' ? (
          <WorldPrimitiveMesh object={object} visual={visual} />
        ) : (
          <WorldAssetMesh object={object} visual={visual} />
        )}
      </group>
      {selected && groupObject ? (
        <TransformControls
          object={groupObject}
          mode={transformMode}
          size={WORLD_TRANSFORM_CONTROL_SIZE}
          onMouseDown={() => {
            onTransformInteraction();
            handleStartTransform();
          }}
          onMouseUp={handleFinishTransform}
          onObjectChange={handleObjectChange}
        />
      ) : null}
    </>
  );
}

interface WorldObjectVisual {
  edgeColor: string;
  edgeOpacity: number;
  emissive: string;
  emissiveIntensity: number;
  meshColor: string;
}

function getWorldObjectVisual(
  state: 'default' | 'hovered' | 'selected',
): WorldObjectVisual {
  if (state === 'selected') {
    return {
      meshColor: '#b7cef5',
      edgeColor: '#1478ff',
      edgeOpacity: 0.72,
      emissive: '#0c2a55',
      emissiveIntensity: 0.18,
    };
  }

  if (state === 'hovered') {
    return {
      meshColor: '#d6d6d6',
      edgeColor: '#242424',
      edgeOpacity: 0.52,
      emissive: '#242424',
      emissiveIntensity: 0.08,
    };
  }

  return {
    meshColor: '#b8b8b8',
    edgeColor: '#555555',
    edgeOpacity: 0.34,
    emissive: '#000000',
    emissiveIntensity: 0,
  };
}

function WorldPrimitiveMesh({
  object,
  visual,
}: {
  object: MotionEditorWorldPrimitiveObject;
  visual: WorldObjectVisual;
}) {
  const geometry = useMemo(
    () => createWorldPrimitiveGeometry(object.primitiveType),
    [object.primitiveType],
  );
  const edgesGeometry = useMemo(() => new EdgesGeometry(geometry), [geometry]);

  useEffect(
    () => () => {
      geometry.dispose();
      edgesGeometry.dispose();
    },
    [edgesGeometry, geometry],
  );

  return (
    <>
      <mesh geometry={geometry} castShadow receiveShadow>
        <meshStandardMaterial
          color={visual.meshColor}
          emissive={visual.emissive}
          emissiveIntensity={visual.emissiveIntensity}
          roughness={0.82}
          metalness={0.02}
        />
      </mesh>
      <lineSegments geometry={edgesGeometry} raycast={ignoreRaycast}>
        <lineBasicMaterial
          color={visual.edgeColor}
          transparent
          opacity={visual.edgeOpacity}
        />
      </lineSegments>
    </>
  );
}

function WorldAssetMesh({
  object,
  visual,
}: {
  object: MotionEditorWorldAssetObject;
  visual: WorldObjectVisual;
}) {
  const [assetRoot, setAssetRoot] = useState<Object3D | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const material = useMemo(
    () =>
      new MeshStandardMaterial({
        roughness: 0.82,
        metalness: 0.02,
      }),
    [],
  );

  useEffect(
    () => () => {
      material.dispose();
    },
    [material],
  );

  useEffect(() => {
    material.color.set(visual.meshColor);
    material.emissive.set(visual.emissive);
    material.emissiveIntensity = visual.emissiveIntensity;
  }, [material, visual.emissive, visual.emissiveIntensity, visual.meshColor]);

  useEffect(() => {
    let active = true;
    let loadedRoot: Object3D | null = null;

    async function loadAsset() {
      setLoadError(null);
      setAssetRoot(null);
      try {
        const root = await loadWorldAssetObject(
          object.assetFormat,
          object.assetUrl,
          object.assetFile,
        );
        if (!active) {
          disposeObject3D(root);
          return;
        }
        root.traverse((child) => {
          if (!isRenderableObject3D(child)) return;
          child.castShadow = true;
          child.receiveShadow = true;
          disposeObjectMaterials(child.material);
          child.material = material;
        });
        floorAndCenterImportedObject(root);
        loadedRoot = root;
        setAssetRoot(root);
      } catch (error) {
        if (!active) return;
        setLoadError(
          error instanceof Error ? error.message : 'Unable to load asset.',
        );
      }
    }

    void loadAsset();

    return () => {
      active = false;
      if (loadedRoot) {
        disposeObject3D(loadedRoot);
      }
    };
  }, [material, object.assetFile, object.assetFormat, object.assetUrl]);

  if (assetRoot) {
    return <primitive object={assetRoot} />;
  }

  return (
    <mesh>
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial
        color={loadError ? '#ff8a8a' : visual.meshColor}
        transparent
        opacity={0.42}
      />
    </mesh>
  );
}

function createWorldPrimitiveGeometry(type: MotionEditorWorldPrimitiveType) {
  if (type === 'plane') {
    const geometry = new PlaneGeometry(1, 1);
    geometry.rotateX(-Math.PI / 2);
    return geometry;
  }
  if (type === 'sphere') return new SphereGeometry(0.5, 32, 18);
  if (type === 'cylinder') return new CylinderGeometry(0.46, 0.46, 1, 32);
  if (type === 'cone') return new ConeGeometry(0.52, 1, 32);
  if (type === 'capsule') return new CapsuleGeometry(0.36, 0.78, 8, 24);
  return new BoxGeometry(1, 1, 1);
}

function pointerFromThreeEvent(
  event: ThreeEvent<PointerEvent>,
): MotionEditorWorldPointer {
  return {
    clientX: event.nativeEvent.clientX,
    clientY: event.nativeEvent.clientY,
    point: [event.point.x, 0, event.point.z],
  };
}

function transformFromObject(
  object: Object3D,
): MotionEditorWorldObjectTransform {
  return {
    position: [object.position.x, object.position.y, object.position.z],
    rotation: [object.rotation.x, object.rotation.y, object.rotation.z],
    scale: [object.scale.x, object.scale.y, object.scale.z],
  };
}
